import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import bb, { line, areaSpline }  from 'billboard.js';
import { BtwDialogModule, BtwDialogService } from '@between/ng-cdk/dialog';
import { BtwButtonModule } from '@between/ng-cdk/button';
import { BankStatementsUploaderComponent } from '../bank-statements-uploader/bank-statements-uploader.component';
import { Guid } from 'guid-typescript';
import { AnalysisApiService } from '../api-services/analysis-api/analysis-api.service';
import { EMPTY, switchMap } from 'rxjs';
import { AuthService } from '../core/services/auth.service';

@Component({
  selector: 'app-app-demo',
  standalone: true,
  imports: [CommonModule, BtwButtonModule, BtwDialogModule, BankStatementsUploaderComponent],
  templateUrl: './app-demo.component.html',
  styleUrl: './app-demo.component.scss'
})
export class AppDemoComponent implements OnInit {
  @ViewChild('analysisId', { static: true }) analysisId!: ElementRef<HTMLInputElement>;
  @ViewChild('apiKey', { static: true }) apiKey!: ElementRef<HTMLInputElement>;

  private readonly dialogService: BtwDialogService = inject(BtwDialogService);
  private readonly analysisApiService: AnalysisApiService = inject(AnalysisApiService);
  private readonly authService: AuthService = inject(AuthService);

  ngOnInit(): void {

  }

  handleUpload(): void {
    const apiKey = this.apiKey.nativeElement.value;
    if (apiKey) {
      this.authService.setApiKey(Guid.parse(apiKey));
    }
    
    this.dialogService.open(BankStatementsUploaderComponent, {
      title: 'Bank statements uploader',
      data: apiKey
    }).afterClosed
    .pipe(
      switchMap((data) => {
        // start loading 
        if (data) {
          const { companyName, uploadReferenceId } = data;
          console.log('companyName: ',companyName);

          return this.analysisApiService.triggerAnalysisFromUploadReferenceId(uploadReferenceId)
        }

        return EMPTY;
      })
      ).subscribe({
        next: (analysisId?: Guid) => {
          // end loading
          if (analysisId) {
            console.log('analysisId: ', analysisId);
          }
        }
      });
  }

  populateSumGraph(indicatorname: string, chartElementId: string, addFlag: boolean) {
    const indicatorDtos: any[] = [];
    const analysisIdValue = this.analysisId.nativeElement.value;
    const apiKeyValue = this.apiKey.nativeElement.value;

    if (analysisIdValue === null || apiKeyValue === null) {
      return;
    }

    fetch('https://localhost:7204/api/analysis?indicatorname=' + indicatorname + '&analysisid=' + analysisIdValue, {
      headers: {
        "ApiKey": apiKeyValue,
      }
    })
      .then(response => response.json())
      .then(data => {
        data.forEach((item: any) => {
          const dto = {
            id: item.id,
            date: item.date,
            name: item.name,
            paymentCodes: item.paymentCodes,
            sumCredit: item.sumCredit,
            sumDebit: item.sumDebit,
            countOfStatements: item.countOfStatements,
            specificIndicatorValues: item.specificIndicatorValues,
            analysisId: item.analysisId
          };
          indicatorDtos.push(dto);
        });

        var index = 0;
        let days: any = [];
        let outcomes: any = [];
        let incomes: any = [];

        // TODO: Below part is hardcoded for presentation purposes

        let regionsStaged: any = [];

        if (addFlag) {
          if (indicatorname == 'Loans') {
            regionsStaged = [
              {
                axis: "x",
                start: '2021-11-01',
                end: '2022-01-01',
                class: "regions_class1",
                label: {
                  text: "Warning: Loan amount installment not stable.",
                  color: "red"
                }
              }
            ]
          }

          if (indicatorname == 'RoundPayments') {
            regionsStaged = [
              {
                axis: "x",
                start: '2022-05-01',
                end: '2022-09-01',
                class: "regions_class2",
                label: {
                  text: "Warning: Sudden increase in round payments.",
                  color: "red"
                }
              }
            ]
          }
        }

        // END TODO

        var chart = bb.generate({
          data: {
            x: "x",
            columns: [
              ["x", ...days],
              ["outflows", ...outcomes],
              ["inflows", ...incomes],
            ],
            type: line(),
            
          },
          tooltip: {
            linked: true
          },
          axis: {
            x: {
              type: "timeseries",
              tick: {
                format: "%Y-%m-%d"
              }
            }
          },
          regions: regionsStaged,
          zoom: {
            enabled: true,
          },
          bindto: "#" + chartElementId,
        });

        indicatorDtos.forEach(dt => {
          days[index] = dt.date;
          outcomes[index] = dt.sumDebit;
          incomes[index] = dt.sumCredit;
          index++;
        })

        setTimeout(function () {
          chart.load({
            columns: [
              ["x", ...days],
              ["outflows", ...outcomes],
              ["inflows", ...incomes],
            ]
          }), 2500
        })

        this.populateCountGraph(indicatorDtos, chartElementId)
        this.populateSpecificIndicatorGraph(0, indicatorDtos, chartElementId)
        this.populateSpecificIndicatorGraph(1, indicatorDtos, chartElementId)
        this.populateSpecificIndicatorGraph(2, indicatorDtos, chartElementId)
        this.populateSpecificIndicatorGraph(3, indicatorDtos, chartElementId)
        this.populateSpecificIndicatorGraph(4, indicatorDtos, chartElementId)
        this.populateSpecificIndicatorGraph(5, indicatorDtos, chartElementId)
      })
  }

  populateCountGraph(indicatorDtos: any, chartElementId: any) {
    var index = 0;
    let days: any = [];
    let countOfStatements: any = [];
    const adjustedChartElementId = chartElementId + "Count";

    var chart = bb.generate({
      data: {
        x: "x",
        columns: [
          ["x", ...days],
          ["# of statements", ...countOfStatements],
        ],
        type: line(), // for ESM specify as: line()
      },
      axis: {
        x: {
          type: "timeseries",
          tick: {
            format: "%Y-%m-%d"
          }
        }
      },
      zoom: {
        enabled: true,
      },
      bindto: "#" + adjustedChartElementId,
      tooltip: {
        linked: true
      },
    });

    indicatorDtos.forEach((dt: any) => {
      days[index] = dt.date;
      countOfStatements[index] = dt.countOfStatements;
      index++;
    })

    setTimeout(function () {
      chart.load({
        columns: [
          ["x", ...days],
          ["# of statements", ...countOfStatements],
        ]
      }), 2500
    });
  }

  populateSpecificIndicatorGraph(specificIndicatorId: any, indicatorDtos: any, chartElementId: any) {
    var index = 0;
    let days: any = [];
    let specificIndicatorValues: any = [];
    const adjustedChartElementId = chartElementId + "Indicator" + specificIndicatorId;
    const specificindicator = indicatorDtos[0].specificIndicatorValues[specificIndicatorId];

    if (specificindicator === undefined) {
      return;
    }

    const specificIndicatorName = specificindicator.name;

    var chart = bb.generate({
      data: {
        x: "x",
        columns: [
          ["x", ...days],
          [specificIndicatorName, ...specificIndicatorValues],
        ],
        type: line(), // for ESM specify as: line()
      },
      axis: {
        x: {
          show: false,
          type: "timeseries",
        }
      },
      zoom: {
        enabled: false,
      },
      bindto: "#" + adjustedChartElementId,
      tooltip: {
        linked: true,
      }
    });

    indicatorDtos.forEach((dt: any) => {
      days[index] = dt.date;
      specificIndicatorValues[index] = dt.specificIndicatorValues[specificIndicatorId].value;
      index++;
    })

    setTimeout(function () {
      chart.load({
        columns: [
          ["x", ...days],
          [specificIndicatorName, ...specificIndicatorValues],
        ]
      }), 2500
    });
  }

  populateFinStatementsChart() {
    const indicatorDtos: any[] = [];

    const analysisIdValue = this.analysisId.nativeElement.value;
    const apiKeyValue = this.apiKey.nativeElement.value;

    if (!analysisIdValue) {
      return;
    }

    fetch('https://localhost:7204/api/analysis?indicatorname=FinancialStatements&analysisid=' + analysisIdValue, {
      headers: {
        "ApiKey": apiKeyValue,
      }
    })
      .then(response => response.json())
      .then(data => {
        data.forEach((item: any) => {
          const indicatorDate = new Date(item.date);
          const dto = {
            id: item.id,
            date: indicatorDate.toLocaleDateString(),
            name: item.name,
            paymentCodes: item.paymentCodes,
            sumCredit: item.sumCredit,
            sumDebit: item.sumDebit,
            countOfStatements: item.countOfStatements,
            specificIndicatorValues: item.specificIndicatorValues,
            analysisId: item.analysisId
          };
          indicatorDtos.push(dto);
        });

        var index = 0;
        let days: any = [];
        let specificIndicatorAOP0009Values: any = [];
        let specificIndicatorAOP0018Values: any = [];
        let specificIndicatorAOP0420Values: any = [];
        let specificIndicatorAOP0422Values: any = [];
        let specificIndicatorAOP0433Values: any = [];
        let specificIndicatorAOP1001Values: any = [];
        let specificIndicatorAOP1003Values: any = [];
        let specificIndicatorAOP1014Values: any = [];
        let specificIndicatorAOP1015Values: any = [];
        let specificIndicatorAOP1016Values: any = [];
        let specificIndicatorAOP1017Values: any = [];
        let specificIndicatorAOP1034Values: any = [];
        let specificIndicatorAOP1041Values: any = [];

        var chart = bb.generate({
          data: {
            x: "x",
            columns: [
              ["x", ...days],
              ["AOP0009", ...specificIndicatorAOP0009Values],
              ["AOP0018", ...specificIndicatorAOP0018Values],
              ["AOP0420", ...specificIndicatorAOP0420Values],
              ["AOP0422", ...specificIndicatorAOP0422Values],
              ["AOP0433", ...specificIndicatorAOP0433Values],
              ["AOP1001", ...specificIndicatorAOP1001Values],
              ["AOP1003", ...specificIndicatorAOP1003Values],
              ["AOP1014", ...specificIndicatorAOP1014Values],
              ["AOP1015", ...specificIndicatorAOP1015Values],
              ["AOP1016", ...specificIndicatorAOP1016Values],
              ["AOP1017", ...specificIndicatorAOP1017Values],
              ["AOP1034", ...specificIndicatorAOP1034Values],
              ["AOP1041", ...specificIndicatorAOP1041Values],
            ],
            types: {
              AOP0009: areaSpline(),
              AOP0018: areaSpline(),
              AOP0420: areaSpline(),
              AOP0422: areaSpline(),
              AOP0433: areaSpline(),
              AOP1001: areaSpline(),
              AOP1003: areaSpline(),
              AOP1014: areaSpline(),
              AOP1015: areaSpline(),
              AOP1016: areaSpline(),
              AOP1017: areaSpline(),
              AOP1034: areaSpline(),
              AOP1041: areaSpline(),
            },
            groups: [
              [
                "AOP0009",
                "AOP0018",
                "AOP0420",
                "AOP0422",
                "AOP0433",
                "AOP1001",
                "AOP1003",
                "AOP1014",
                "AOP1015",
                "AOP1016",
                "AOP1017",
                "AOP1034",
                "AOP1041",
              ]
            ]
          },
          axis: {
            x: {
              type: "category"
            }
          },
          bindto: "#tableView"
        });

        indicatorDtos.forEach(dt => {
          days[index] = dt.date;
          specificIndicatorAOP0009Values[index] = dt.specificIndicatorValues[0].value;
          specificIndicatorAOP0018Values[index] = dt.specificIndicatorValues[1].value;
          specificIndicatorAOP0420Values[index] = dt.specificIndicatorValues[2].value;
          specificIndicatorAOP0422Values[index] = dt.specificIndicatorValues[3].value;
          specificIndicatorAOP0433Values[index] = dt.specificIndicatorValues[4].value;
          specificIndicatorAOP1001Values[index] = dt.specificIndicatorValues[5].value;
          specificIndicatorAOP1003Values[index] = dt.specificIndicatorValues[6].value;
          specificIndicatorAOP1014Values[index] = dt.specificIndicatorValues[7].value;
          specificIndicatorAOP1015Values[index] = dt.specificIndicatorValues[8].value;
          specificIndicatorAOP1016Values[index] = dt.specificIndicatorValues[9].value;
          specificIndicatorAOP1017Values[index] = dt.specificIndicatorValues[10].value;
          specificIndicatorAOP1034Values[index] = dt.specificIndicatorValues[11].value;
          specificIndicatorAOP1041Values[index] = dt.specificIndicatorValues[12].value;
          index++;
        })

        this.generateFinStatementsTable(indicatorDtos);

        setTimeout(function () {
          chart.load({
            columns: [
              ["x", ...days],
              ["AOP0009", ...specificIndicatorAOP0009Values],
              ["AOP0018", ...specificIndicatorAOP0018Values],
              ["AOP0420", ...specificIndicatorAOP0420Values],
              ["AOP0422", ...specificIndicatorAOP0422Values],
              ["AOP0433", ...specificIndicatorAOP0433Values],
              ["AOP1001", ...specificIndicatorAOP1001Values],
              ["AOP1003", ...specificIndicatorAOP1003Values],
              ["AOP1014", ...specificIndicatorAOP1014Values],
              ["AOP1015", ...specificIndicatorAOP1015Values],
              ["AOP1016", ...specificIndicatorAOP1016Values],
              ["AOP1017", ...specificIndicatorAOP1017Values],
              ["AOP1034", ...specificIndicatorAOP1034Values],
              ["AOP1041", ...specificIndicatorAOP1041Values],
            ]
          }), 2500
        });
      });
  }

  generateFinStatementsTable(indicatorDtos: any) {
    const tableContainer = document.getElementById('finStatementsTable');

    const table = document.createElement('table');
    const headerRow = document.createElement('tr');

    const th = document.createElement('th');
    th.innerText = "Position"
    headerRow.appendChild(th);

    for (let i = 0; i < indicatorDtos.length; i++) {
      const th = document.createElement('th');
      const indicatorDate = new Date(indicatorDtos[i].date);
      th.innerText = indicatorDate.toLocaleDateString();
      headerRow.appendChild(th);
    }

    table.appendChild(headerRow);

    for (let i = 0; i < indicatorDtos[0].specificIndicatorValues.length; i++) {
      const row = document.createElement('tr');

      const cell = document.createElement('td');
      cell.innerText = indicatorDtos[0].specificIndicatorValues[i].name;
      row.appendChild(cell)

      for (let j = 0; j < indicatorDtos.length; j++) {
        const cell = document.createElement('td');
        cell.innerText = Number(indicatorDtos[j].specificIndicatorValues[i].value).toLocaleString();
        row.appendChild(cell)
      }

      // Add the row to the table
      table.appendChild(row);
    }

    tableContainer!.appendChild(table);
  }
}
